/**
 * Aviso de co-deudor en la venta a crédito y la solicitud de crédito.
 *
 * Con Configuración → "Tratamiento de co-deudor" encendido, al completar un
 * documento (del cliente o del fiador) se consulta /api/codeudor_prestamos y,
 * si esa persona ya garantiza préstamos vivos de otros, se pinta la lista.
 * Con el flag apagado el endpoint responde activo=false y aquí no sale nada.
 *
 * Es solo el aviso: en la venta a crédito la autorización la exige el
 * backend al guardar (409 requiereAutorizacion), con el mismo flujo de
 * siempre. Igual que AlertaRed, corre aparte del autollenado y cualquier
 * fallo se traduce en no mostrar nada.
 */
import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import { documentoValido } from "lib/documento";
import { fmtMoney } from "lib/moneda";
import { useApi } from "lib/api";

const ESPERA_MS = 500;

export default function AlertaCodeudor({ cedula, tipo, quien = "cliente", sx = {} }) {
  const { post } = useApi();
  const [prestamos, setPrestamos] = useState([]);

  const doc = String(cedula || "").trim();
  const completo = documentoValido(doc, tipo);

  useEffect(() => {
    if (!completo) {
      setPrestamos([]);
      return undefined;
    }
    // `vivo` descarta la respuesta de un documento que ya cambió.
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const { ok, data } = await post("/api/codeudor_prestamos", { cedula: doc });
        if (vivo) setPrestamos(ok && data.activo && Array.isArray(data.prestamos) ? data.prestamos : []);
      } catch (e) {
        if (vivo) setPrestamos([]);
      }
    }, ESPERA_MS);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, completo]);

  if (!prestamos.length) return null;

  const sujeto = quien === "fiador" ? "El fiador" : "El cliente";
  const n = prestamos.length;
  return (
    <Alert severity="warning" sx={{ mt: 0.75, borderRadius: 2, ...sx }}>
      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700 }}>
        {sujeto} ya es co-deudor de {n} {n === 1 ? "préstamo" : "préstamos"}
      </Typography>
      {prestamos.slice(0, 5).map((p) => (
        <Typography key={p.noprest} sx={{ fontSize: "0.78rem" }}>
          #{p.noprest} · {p.deudor || p.cedula} · RD$ {fmtMoney(p.balance)}
          {p.status === "I" ? " · incobrable" : ""}
          {p.cuotasatrasadas > 0
            ? ` · ${p.cuotasatrasadas} ${p.cuotasatrasadas === 1 ? "cuota atrasada" : "cuotas atrasadas"}`
            : ""}
        </Typography>
      ))}
      {n > 5 && <Typography sx={{ fontSize: "0.78rem" }}>y {n - 5} más</Typography>}
    </Alert>
  );
}
